import _ from 'lodash'
import { twMerge } from 'tailwind-merge'
import { useEffect, useState, useCallback, useRef } from 'react'
import { vehicleApi } from '@/api'
import { Vehicle } from '@cruz-com/lookup'
import { Section } from './vehicleFilterSection'
import { Checkbox, Text } from '@/components'
import { MakeModelListBox } from './vehicleMakeModelListBox'
import { useLocalStorageState } from 'ahooks'
import { PlusIcon, MinusIcon } from '@heroicons/react/20/solid'

type mmQuery = {
  make?: string
  model?: string | null
  trim?: string | null
}

type Props = {
  category?: string
  bodyTypeId?: string | number
  defaultOpen?: boolean
  className?: string
  name: string
  title: string
  otherQuery: Vehicle.Query
  mmt: string
  makeOptions: any
  onChange: (val: any) => void
} & (React.ComponentPropsWithoutRef<'select'>)

const ignoreFilters = ['mmt1', 'mmt2', 'mmt3', 'make', 'model', 'trim']
const mmStyle = 'text-sm/10 w-[60px] font-medium'

export function MakeModeTrim({ defaultOpen, className, name, title, category, bodyTypeId, otherQuery, mmt, makeOptions, onChange }: Props) {
  function getQueryFromMmt() {
    if (!mmt) {
      return { make: '' }
    }
    const y = _.split(mmt, ',')
    return { make: y[0], model: y[1], trim: y[2] }
  }

  const sectionRef = useRef<null | HTMLDivElement>(null)
  const [query, setQuery] = useState<mmQuery>(getQueryFromMmt())
  const [isMakeChanged, setIsMakeChanged] = useState(false) // used for scroll into view
  const [modelOption, setModelOption] = useState<any>(null)
  const [modelKeyword, setModelKeyword] = useState('')
  const [showOtherModel, setShowOtherModel] = useLocalStorageState<boolean>(`mmt-${name}`, { defaultValue: false })
  const [trimOption, setTrimOption] = useState<any>(null)

  // otherQuery Changed
  useEffect(() => {
    if (query?.make) {
      getModelOption()
    } else {
      setModelOption({})
      setTrimOption([])
    }
  }, [otherQuery])

  // mmt changed
  useEffect(() => {
    setQuery(getQueryFromMmt())
  }, [mmt])

  // update parent
  useEffect(() => {
    if (query?.make) {
      onChange(_.compact([query.make, query?.model, query?.trim]).join(','))
    } else {
      onChange(null)
    }
  }, [query])

  // make changed
  function makeChanged(val: string | any) {
    setQuery({ make: val, model: null, trim: null })
  }

  useEffect(() => {
    setModelOption(null)
    setTrimOption(null)
    setIsMakeChanged(true)
    if (query?.make) {
      getModelOption()
    }
  }, [query?.make])

  // models
  // get model option
  async function getModelOption() {
    const q = { ..._.omit(otherQuery, ignoreFilters), make: query.make }
    if (bodyTypeId) {
      _.assign(q, { body_type: bodyTypeId })
    }
    const d = await vehicleApi.getModelOption(q, category)
    setModelOption(d)
  }

  useEffect(() => {
    if (!_.isEmpty(modelOption) && isMakeChanged) {
      sectionRef?.current?.scrollIntoView({ behavior: 'smooth' })
      setIsMakeChanged(false)
    }
  }, [modelOption])

  function modelSelected(model: string, val: boolean) {
    const x: string[] = query?.model ? _.split(query.model as string, '|') : []
    const y = val ? _.concat(x, [model]) : _.without(x, model)
    setQuery({ ...query, model: _.join(y, '|') })
  }

  useEffect(() => {
    if (_.isEmpty(query?.model)) {
      setQuery({ ...query, trim: null })
      setTrimOption([])
    }
  }, [query?.model])

  const filteredPopular = useCallback(() => {
    if (_.isEmpty(modelKeyword)) { return modelOption?.popular }
    return _.filter(modelOption?.popular, (x: string) => x.toLowerCase().includes(modelKeyword.toLowerCase()))
  }, [modelOption?.popular, modelKeyword])

  const filteredOther = useCallback(() => {
    if (_.isEmpty(modelKeyword)) { return modelOption?.other }
    return _.filter(modelOption?.other, (x: string) => x.toLowerCase().includes(modelKeyword.toLowerCase()))
  }, [modelOption?.other, modelKeyword])

  const Models = () => {
    if (!modelOption || (_.isEmpty(modelOption?.popular) && _.isEmpty(modelOption?.other))) {
      return null
    }
    const showTitle = (modelOption?.popular.length > 0 && modelOption?.other.length > 0)

    // popular models
    const PopularModels = () => {
      if (_.isEmpty(modelOption?.popular)) { return null }
      return (
        <div className='mt-4'>
          {
            showTitle &&
            <div className='text-sm font-medium mt-3 pb-1.5 border-b w-full'>
              Popular Models <span className='font-light'>({modelOption?.popular.length})</span>
            </div>
          }
          <ul className='grid grid-cols-1 md:grid-cols-2 gap-x-4 mt-1'>
            {
              _.map(filteredPopular(), (name, idx) => {
                const x = _.split(query?.model, '|')
                return (
                  <li key={idx}>
                    <Checkbox name={name} label={name} checked={x.includes(name)} onChange={val => modelSelected(name, val)} />
                  </li>)
              })
            }
          </ul>
        </div>
      )
    }

    const OtherModels = () => {
      if (_.isEmpty(modelOption?.other)) { return null }
      return (
        <div className='mt-3'>
          {
            showTitle &&
            <>
              <button type='button'
                className='w-full text-sm font-medium mt-4 pb-1.5 border-b flex justify-between items-bottom'
                onClick={() => setShowOtherModel(!showOtherModel)}
              >
                <div>Other Models <span className='font-light'>({modelOption?.other.length})</span></div>
                {showOtherModel ? <MinusIcon className='size-4' /> : <PlusIcon className='size-4' />}
              </button>
              <div className={twMerge('text-sm font-light mt-1 text-zinc-500', (showOtherModel) && 'hidden')}>
                Click "Other Models" to see more ...
              </div>
            </>
          }
          <ul className={twMerge('grid grid-cols-1 md:grid-cols-2 gap-x-4 mt-1', (!showTitle || showOtherModel) ? '' : 'hidden')}>
            {
              _.map(filteredOther(), (name, idx) => {
                const x = _.split(query?.model, '|')
                return (
                  <li key={idx}>
                    <Checkbox name={name} label={name} checked={x.includes(name)} onChange={val => modelSelected(name, val)} />
                  </li>)
              })
            }
          </ul>
        </div>
      )
    }

    return (
      <div className='flex items-start w-full'>
        <div className={mmStyle}>Models:</div>
        <div className='flex-1'>
          <div className='mb-3 w-full md:w-[200px]'>
            <Text placeholder='Search Model' compact={true} name='mf' type='text' value={modelKeyword} onChange={e => setModelKeyword(e.target.value || '')} />
          </div>
          <PopularModels />
          <OtherModels />
        </div>
      </div>
    )
  }

  // trims
  useEffect(() => {
    setTrimOption(null)
    if (!_.isEmpty(query?.model)) {
      getTrimOption()
    }
  }, [query?.model])

  // get trim options
  async function getTrimOption() {
    const q = { ..._.omit(otherQuery, ignoreFilters), make: query.make, model: query.model }
    if (bodyTypeId) {
      _.assign(q, { body_type: bodyTypeId })
    }
    const d = await vehicleApi.getTrimOption(q, category)
    setTrimOption(d)
  }

  function trimSelected(trim: string, val: boolean) {
    const x: string[] = query?.trim ? _.split(query.trim as string, '|') : []
    const y = val ? _.concat(x, [trim]) : _.without(x, trim)
    setQuery({ ...query, trim: _.join(y, '|') })
  }

  const Trims = () => {
    if (_.isEmpty(trimOption)) { return <></> }
    return (
      <div className='flex items-start w-full'>
        <div className={mmStyle}>Trims:</div>
        <ul className='flex-1 grid grid-cols-1 md:grid-cols-2 gap-x-4 mt-1'>
          {
            _.map(trimOption, (name, idx) => {
              const x = _.split(query?.trim, '|')
              return (
                <li key={idx}>
                  <Checkbox name={name} label={name} checked={x.includes(name)} onChange={val => trimSelected(name, val)} />
                </li>)
            })
          }
        </ul>
      </div>
    )
  }

  return (
    <div ref={sectionRef}>
      <Section name={name} title={title} hasValue={!_.isEmpty(query?.make)} className={className} defaultOpen={defaultOpen}>
        <div className='flex flex-col gap-y-3 w-full' ref={sectionRef}>
          <div className='flex items-start' ref={sectionRef}>
            <div className={mmStyle}>Make:</div>
            <div className='w-[220px]'>
              <MakeModelListBox value={query?.make || 'Any'} allowAny={true} option={makeOptions} onChange={val => makeChanged(val)} />
            </div>
          </div>
          {Models()}
          <Trims />
        </div>
      </Section>
    </div>
  )
}